/**
 * Tenant Feature Flags Context
 * Controls which features are available for the current tenant
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_BASE } from '../config/api';

const API = API_BASE;

// Default feature flags (all enabled for master)
const DEFAULT_FEATURES = {
  courses: true,
  learning_labs: true,
  learning_pathways: true,
  masterclasses: true,
  ebooks: true,
  wsp: true,
  certificates: false,  // Removed per requirements
  navi_coach: true,
  ai_course_generation: false,
  cpd_tracking: true,
  campaigns: true,  // Organization Campaigns
};

const FeatureFlagsContext = createContext({
  features: DEFAULT_FEATURES,
  loading: true,
  isFeatureEnabled: (feature) => true,
  refreshFeatures: async () => {}
});

export function FeatureFlagsProvider({ children }) {
  const { user } = useAuth();
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [loading, setLoading] = useState(true);

  // Fetch feature flags when organization changes
  useEffect(() => {
    fetchFeatures();
  }, [user?.organization_id]);

  const fetchFeatures = async () => {
    const orgId = user?.organization_id;
    
    // If no org, use defaults (master platform)
    if (!orgId) {
      setFeatures(DEFAULT_FEATURES);
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API}/api/organizations/${orgId}/features`);
      setFeatures(res.data.features || DEFAULT_FEATURES);
    } catch (error) {
      console.error('Failed to fetch feature flags:', error);
      setFeatures(DEFAULT_FEATURES);
    } finally {
      setLoading(false);
    }
  };

  // Check if a specific feature is enabled
  const isFeatureEnabled = (feature) => {
    return features[feature] !== false; // Default to true if not specified
  };

  return (
    <FeatureFlagsContext.Provider value={{
      features,
      loading,
      isFeatureEnabled,
      refreshFeatures: fetchFeatures
    }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}

/**
 * HOC to wrap components that should only render if feature is enabled
 */
export function withFeatureFlag(WrappedComponent, featureName, FallbackComponent = null) {
  return function FeatureFlaggedComponent(props) {
    const { isFeatureEnabled } = useFeatureFlags();
    
    if (!isFeatureEnabled(featureName)) {
      return FallbackComponent ? <FallbackComponent {...props} /> : null;
    }
    
    return <WrappedComponent {...props} />;
  };
}

/**
 * Component that conditionally renders children based on feature flag
 */
export function FeatureFlag({ feature, children, fallback = null }) {
  const { isFeatureEnabled } = useFeatureFlags();
  
  if (!isFeatureEnabled(feature)) {
    return fallback;
  }
  
  return children;
}

export default FeatureFlagsContext;
